import { takeLatest, takeLeading, spawn, put, call, delay } from 'redux-saga/effects';

/* Project */
import apiRequest, { apiSuccess, update } from 'utils/api';
import DiscrepancyDetailsTypes from 'screens/DiscrepancyDetail/DiscrepancyDetails.types';
import { v4 as uuidv4 } from 'uuid';
import * as APP from 'commons/commons.reducer';
import { SET_ERROR, DELETE_DOCUMENT_SUCCESS } from 'commons/commons.reducer';
import { openGeneralAlert } from 'commons/commons.actions';
import controlledCall from 'utils/services/controlledSaga';
import { selectState } from 'utils/reduxActions';
import { pairFiles, downloadAttachment } from 'utils/functions';
import { formatDistanceToNow } from 'date-fns';
import {
  DEFAULT_RETRY_CONFIG,
  calculateBackoffDelay,
  sleep,
  isRecoverableError,
  reportUploadError,
  reportUploadSuccess,
  UploadErrorType,
} from 'utils/retryUtils';

function* getDiscrepancy() {
  yield takeLatest(DiscrepancyDetailsTypes.GET_DISCREPANCY, function* getDiscrepancyApi(action) {
    const { id, history } = action.payload;

    const response = yield apiRequest(`api/v1/discrepancies/${id}`, { method: 'get' }, true, true);

    if (!response.error) {
      yield put(apiSuccess(DiscrepancyDetailsTypes.GET_DISCREPANCY_SUCCESS, response));
    } else {
      yield put(apiSuccess(DiscrepancyDetailsTypes.GET_DISCREPANCY_FAIL));
      history.replace('/');
    }
  });
}

function* getHearingParticipationsFile() {
  yield takeLatest(
    DiscrepancyDetailsTypes.GET_HEARING_PARTICIPATIONS_FILE,
    function* getDiscrepancyApi(action) {
      const { id } = action.payload;

      const response = yield apiRequest(
        `api/v1/discrepancies/xlsx/${id}`,
        { method: 'get' },
        true,
        true,
      );

      if (!response.error) {
        yield put(
          apiSuccess(DiscrepancyDetailsTypes.GET_HEARING_PARTICIPATIONS_FILE_SUCCESS, response),
        );
      } else {
        yield put(apiSuccess(DiscrepancyDetailsTypes.GET_HEARING_PARTICIPATIONS_FILE_FAIL));
      }
    },
  );
}

function* downloadAllFilesSaga() {
  yield takeLatest(DiscrepancyDetailsTypes.DOWNLOAD_ALL_FILES_REQUEST, function* blabla(action) {
    const { data } = action.payload.data;

    // eslint-disable-next-line no-restricted-syntax
    for (const file of data) {
      // The helper reports the failure to Sentry on its own, but without an
      // `onError` the user is told nothing when a file in the batch fails.
      let downloadAlert = null;

      // These files live in our bucket, so a signature that expired while the
      // discrepancy sat open is refreshed instead of failing with a 403.
      yield call(downloadAttachment, file, {
        onError: (alert) => {
          downloadAlert = alert;
        },
      });

      if (downloadAlert) {
        yield put(openGeneralAlert(downloadAlert));
      }

      yield delay(300);
    }
  });
}

function* makeHearingParticipationsFile() {
  yield takeLatest(
    DiscrepancyDetailsTypes.MAKE_HEARING_PARTICIPATIONS_FILE,
    function* getDiscrepancyApi(action) {
      const { id } = action.payload;

      const response = yield apiRequest(
        `api/v1/discrepancies/xlsx/make/${id}`,
        { method: 'get' },
        true,
        true,
      );

      if (!response.error) {
        yield put(
          apiSuccess(DiscrepancyDetailsTypes.MAKE_HEARING_PARTICIPATIONS_FILE_SUCCESS, response),
        );
      } else {
        yield put(apiSuccess(DiscrepancyDetailsTypes.MAKE_HEARING_PARTICIPATIONS_FILE_FAIL));
      }
    },
  );
}

function* postDocumentDirectS3() {
  yield takeLeading(DiscrepancyDetailsTypes.CREATE_DOCUMENT_DIRECT_S3, function* postDocumentDirectS3Api(action) {
    const { history, ...payload } = action.payload;
    const payloadCopy = { ...payload };

    const { files, attachments } = payloadCopy;

    delete payloadCopy.files;
    delete payloadCopy.attachments;

    // Generate idempotency key to prevent duplicates
    const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(7)}`;

    // Prepare file metadata for backend
    const fileMetadata = [];
    const fileMapping = []; // Mapping between valid files and their metadata

    // Process main files - only add files that exist (no null values)
    const [publicFile, wordFile, confidentialFile] = files;
    if (publicFile) {
      fileMetadata.push({
        name: publicFile.name,
        size: publicFile.size,
        type: publicFile.type,
      });
      fileMapping.push({ type: 'public', file: publicFile });
    }
    if (wordFile) {
      fileMetadata.push({
        name: wordFile.name,
        size: wordFile.size,
        type: wordFile.type,
      });
      fileMapping.push({ type: 'word', file: wordFile });
    }
    if (confidentialFile) {
      fileMetadata.push({
        name: confidentialFile.name,
        size: confidentialFile.size,
        type: confidentialFile.type,
      });
      fileMapping.push({ type: 'confidential', file: confidentialFile });
    }

    // Process attachments
    const attachmentMetadata = [];
    attachments.forEach((attachment) => {
      if (attachment) {
        const { privateAtt, publicAtt } = attachment;
        const attachmentData = {};

        if (publicAtt) {
          attachmentData.publicAtt = {
            name: publicAtt.name,
            size: publicAtt.size,
            type: publicAtt.type,
          };
        }

        if (privateAtt) {
          attachmentData.privateAtt = {
            name: privateAtt.name,
            size: privateAtt.size,
            type: privateAtt.type,
          };
        }

        attachmentMetadata.push(attachmentData);
      }
    });

    // Prepare payload for backend
    const preparePayload = {
      ...payloadCopy,
      files: fileMetadata,
      attachments: attachmentMetadata,
      useDirectS3Upload: true,
      idempotencyKey, // Add idempotency key
    };
    console.log('preparePayload', preparePayload);
    try {
      // Step 1: Prepare S3 upload - create document and get presigned URLs
      const prepareResponse = yield apiRequest('api/v1/documents', {
        method: 'post',
        body: JSON.stringify({ jsonBody: JSON.stringify(preparePayload) }),
        headers: {
          'Content-Type': 'application/json',
        },
      }, true, true);

      if (prepareResponse.error) {
        yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_FAIL, prepareResponse));
        yield put(apiSuccess(APP.SET_ERROR, { msg: prepareResponse.errorMsg }));
        return;
      }

      const { uploadUrls, newDocumentId, alreadyProcessed } = prepareResponse;

      // Check if this was a duplicate request
      if (alreadyProcessed) {
        console.log('[IDEMPOTENCY] Document already exists, skipping upload');
        yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_SUCCESS, prepareResponse));
        yield put(apiSuccess(APP.SET_SUCCESS, { msg: 'Documento ya existe' }));
        if (history) history.push(`/discrepancy/${payloadCopy.discrepancyId}`);
        return;
      }

      // Step 2: Upload files directly to S3 - SEQUENTIAL UPLOAD FOR MAIN FILES
      const uploadResults = [];

      // Flatten attachment files into a single array with metadata
      const attachmentFiles = [];
      attachments.forEach((attachment, attIndex) => {
        if (attachment) {
          if (attachment.publicAtt) {
            attachmentFiles.push({
              file: attachment.publicAtt,
              isPublic: true,
              isPrivate: false,
              originalIndex: attIndex
            });
          }
          if (attachment.privateAtt) {
            attachmentFiles.push({
              file: attachment.privateAtt,
              isPublic: false,
              isPrivate: true,
              originalIndex: attIndex
            });
          }
        }
      });

      console.log('[UPLOAD] Starting file upload process');
      console.log('[UPLOAD] Main file mapping:', fileMapping.map(f => ({ type: f.type, name: f.file.name })));
      console.log('[UPLOAD] Attachment files:', attachmentFiles.map(af => ({ name: af.file.name, isPublic: af.isPublic })));
      console.log('[UPLOAD] Upload URLs:', uploadUrls.map(u => ({ fileName: u.fileName, isMainFile: u.isMainFile })));

      // Helper function to upload a single file with retry and exponential backoff
      const uploadFileWithRetry = function* (urlInfo, file, config = DEFAULT_RETRY_CONFIG) {
        const { maxRetries, timeoutMs } = config;
        const startTime = Date.now();

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            console.log(`[UPLOAD] Attempt ${attempt + 1}/${maxRetries} - Uploading ${urlInfo.fileName} to S3`);

            // Add timeout to detect slow/broken connections
            const s3Response = yield Promise.race([
              fetch(decodeURI(urlInfo.presignedUrl), {
                method: 'PUT',
                headers: {
                  'Content-Type': urlInfo.contentType,
                },
                body: file,
              }),
              new Promise((_, reject) =>
                setTimeout(
                  () => reject(new Error(`Upload timeout - la conexión tardó más de ${Math.round(timeoutMs / 1000)} segundos`)),
                  timeoutMs
                )
              ),
            ]);

            if (s3Response.ok) {
              console.log(`[UPLOAD] Success: ${urlInfo.fileName}`);
              // Report success if it took retries
              if (attempt > 0) {
                reportUploadSuccess({
                  operation: UploadErrorType.S3_UPLOAD,
                  fileName: file.name,
                  attempt: attempt + 1,
                  totalTime: Date.now() - startTime,
                });
              }
              return {
                attachmentId: urlInfo.attachmentId,
                s3Key: urlInfo.s3Key,
                uploadSuccess: true,
              };
            }

            const statusCode = s3Response.status;
            const errorText = yield s3Response.text().catch(() => 'Unknown error');
            console.error(`[UPLOAD] Attempt ${attempt + 1} failed: ${statusCode} ${s3Response.statusText}`, errorText);

            // Report error to Sentry
            reportUploadError({
              operation: UploadErrorType.S3_UPLOAD,
              fileName: file.name,
              fileSize: file.size,
              contentType: urlInfo.contentType,
              attempt: attempt + 1,
              maxRetries,
              error: errorText,
              statusCode,
              attachmentId: urlInfo.attachmentId,
              documentId: newDocumentId,
              isFinalAttempt: attempt === maxRetries - 1,
            });

            // Check if error is recoverable
            const recoverable = isRecoverableError(errorText, statusCode);
            if (!recoverable && attempt > 0) {
              console.warn(`[UPLOAD] Non-recoverable error detected, stopping retries`);
              return {
                attachmentId: urlInfo.attachmentId,
                s3Key: urlInfo.s3Key,
                uploadSuccess: false,
                error: `Error HTTP ${statusCode}: ${errorText}`,
              };
            }

            if (attempt < maxRetries - 1) {
              // Wait before retry with exponential backoff and jitter
              const delayMs = calculateBackoffDelay(attempt, config);
              console.log(`[UPLOAD] Waiting ${Math.round(delayMs)}ms before retry...`);
              yield call(sleep, delayMs);
            }
          } catch (error) {
            console.error(`[UPLOAD] Attempt ${attempt + 1} error:`, error);

            // Report error to Sentry
            reportUploadError({
              operation: UploadErrorType.S3_UPLOAD,
              fileName: file.name,
              fileSize: file.size,
              contentType: urlInfo.contentType,
              attempt: attempt + 1,
              maxRetries,
              error,
              attachmentId: urlInfo.attachmentId,
              documentId: newDocumentId,
              isFinalAttempt: attempt === maxRetries - 1,
            });

            if (attempt === maxRetries - 1) {
              return {
                attachmentId: urlInfo.attachmentId,
                s3Key: urlInfo.s3Key,
                uploadSuccess: false,
                error: error.message,
              };
            }

            // Wait before retry with exponential backoff
            const delayMs = calculateBackoffDelay(attempt, config);
            console.log(`[UPLOAD] Waiting ${Math.round(delayMs)}ms before retry...`);
            yield call(sleep, delayMs);
          }
        }

        return {
          attachmentId: urlInfo.attachmentId,
          s3Key: urlInfo.s3Key,
          uploadSuccess: false,
          error: `Máximo de ${maxRetries} reintentos alcanzado`,
        };
      };

      // PHASE 1: Upload main files SEQUENTIALLY
      const mainFileUrls = uploadUrls.filter(u => u.isMainFile);
      let attachmentFileIndex = 0;

      console.log('[UPLOAD] Phase 1: Uploading main files sequentially');
      for (const urlInfo of mainFileUrls) {
        const mappedFile = fileMapping[urlInfo.fileIndex];

        if (!mappedFile || !mappedFile.file) {
          console.error(`[UPLOAD] File not found for index ${urlInfo.fileIndex}`);
          uploadResults.push({
            attachmentId: urlInfo.attachmentId,
            s3Key: urlInfo.s3Key,
            uploadSuccess: false,
            error: 'File not found in mapping',
          });
          continue;
        }

        console.log(`[UPLOAD] Uploading main file: ${mappedFile.type} - ${mappedFile.file.name}`);
        const result = yield* uploadFileWithRetry(urlInfo, mappedFile.file);
        uploadResults.push(result);

        // If main file failed, abort entire upload
        if (!result.uploadSuccess) {
          console.error(`[UPLOAD] CRITICAL: Main file ${mappedFile.type} failed to upload. Aborting.`);
          yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_FAIL, { error: true }));
          yield put(apiSuccess(APP.SET_ERROR, { msg: `Error al cargar archivo ${mappedFile.type}. Por favor intente nuevamente.` }));
          return;
        }
      }

      console.log('[UPLOAD] Phase 1 completed: All main files uploaded successfully');

      // PHASE 2: Upload attachment files (can fail without aborting)
      const attachmentFileUrls = uploadUrls.filter(u => !u.isMainFile);

      console.log('[UPLOAD] Phase 2: Uploading attachment files');
      for (const urlInfo of attachmentFileUrls) {
        const attachmentFile = attachmentFiles[attachmentFileIndex];
        attachmentFileIndex++;

        if (!attachmentFile || !attachmentFile.file) {
          console.warn(`[UPLOAD] Attachment file not found at index ${attachmentFileIndex - 1}`);
          uploadResults.push({
            attachmentId: urlInfo.attachmentId,
            s3Key: urlInfo.s3Key,
            uploadSuccess: false,
            error: 'Attachment file not found',
          });
          continue;
        }

        console.log(`[UPLOAD] Uploading attachment: ${attachmentFile.file.name}`);
        const result = yield* uploadFileWithRetry(urlInfo, attachmentFile.file);
        uploadResults.push(result);
      }

      console.log('[UPLOAD] Phase 2 completed');
      console.log('[UPLOAD] Upload summary:', {
        total: uploadResults.length,
        successful: uploadResults.filter(r => r.uploadSuccess).length,
        failed: uploadResults.filter(r => !r.uploadSuccess).length,
      });

      // Step 3: ALWAYS confirm upload to validate S3 existence - WITH RETRY
      console.log('[CONFIRM] Validating uploads with backend');

      // Helper function to confirm uploads with retry
      const confirmWithRetry = function* (config = DEFAULT_RETRY_CONFIG) {
        const { maxRetries } = config;
        const startTime = Date.now();

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            console.log(`[CONFIRM] Attempt ${attempt + 1}/${maxRetries}`);

            const response = yield apiRequest('api/v1/documents/confirm-s3-upload', {
              method: 'post',
              body: JSON.stringify({
                documentId: newDocumentId,
                uploadedFiles: uploadResults,
                emailContext: prepareResponse.emailContext,
              }),
              headers: {
                'Content-Type': 'application/json',
              },
            }, true, true);

            if (!response?.error) {
              console.log(`[CONFIRM] Success on attempt ${attempt + 1}`);
              // Report success if it took retries
              if (attempt > 0) {
                reportUploadSuccess({
                  operation: UploadErrorType.CONFIRMATION,
                  fileName: `document_${newDocumentId}`,
                  attempt: attempt + 1,
                  totalTime: Date.now() - startTime,
                });
              }
              return { success: true, data: response };
            }

            const errorMsg = response?.errorMsg || 'Error desconocido en confirmación de upload';
            console.error(`[CONFIRM] Attempt ${attempt + 1} failed:`, errorMsg);

            // Report error to Sentry
            reportUploadError({
              operation: UploadErrorType.CONFIRMATION,
              fileName: `document_${newDocumentId}`,
              fileSize: 0,
              contentType: 'application/json',
              attempt: attempt + 1,
              maxRetries,
              error: errorMsg,
              documentId: newDocumentId,
              isFinalAttempt: attempt === maxRetries - 1,
            });

            // Check if error is recoverable (network issues, timeouts, etc.)
            const recoverable = isRecoverableError(errorMsg);

            if (attempt < maxRetries - 1 && recoverable) {
              const delayMs = calculateBackoffDelay(attempt, config);
              console.log(`[CONFIRM] Waiting ${Math.round(delayMs)}ms before retry...`);
              yield call(sleep, delayMs);
            } else if (!recoverable) {
              // Non-recoverable error, don't retry
              console.warn('[CONFIRM] Non-recoverable error, stopping retries');
              return { success: false, error: response.errorMsg, data: response };
            }
          } catch (error) {
            console.error(`[CONFIRM] Attempt ${attempt + 1} exception:`, error);

            // Report error to Sentry
            reportUploadError({
              operation: UploadErrorType.CONFIRMATION,
              fileName: `document_${newDocumentId}`,
              fileSize: 0,
              contentType: 'application/json',
              attempt: attempt + 1,
              maxRetries,
              error,
              documentId: newDocumentId,
              isFinalAttempt: attempt === maxRetries - 1,
            });

            if (attempt < maxRetries - 1) {
              const delayMs = calculateBackoffDelay(attempt, config);
              console.log(`[CONFIRM] Waiting ${Math.round(delayMs)}ms before retry...`);
              yield call(sleep, delayMs);
            } else {
              return { success: false, error: error.message };
            }
          }
        }

        return { success: false, error: `Máximo de ${maxRetries} reintentos alcanzado` };
      };

      const confirmResult = yield* confirmWithRetry();

      if (!confirmResult.success) {
        const confirmError = confirmResult.error || 'Error desconocido';
        console.error('[CONFIRM] Validation failed after retries:', confirmError);
        yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_FAIL, confirmResult.data || {}));
        yield put(apiSuccess(APP.SET_ERROR, { msg: `Error validando archivos en S3: ${confirmError}. Por favor intente nuevamente.` }));
        return;
      }

      console.log('[CONFIRM] All files validated successfully in S3');

      // Step 4: Generate view URLs for all successfully uploaded attachments
      const successfulUploads = uploadResults.filter(result => result.uploadSuccess);
      for (const uploadResult of successfulUploads) {
        const attachment = Object.values(prepareResponse.objects.attachments).find(
          att => att.id === uploadResult.attachmentId
        );
        
        if (attachment) {
          try {
            const viewUrlBody = JSON.stringify({
              id: attachment.id,
              fileName: attachment.content,
              contentType: attachment.contentType,
              attachment: true,
            });
            
            const viewFileSignedUrlResponse = yield apiRequest(
              `api/v1/s3/get_presigned_url_view`,
              {
                method: 'post',
                body: viewUrlBody,
              },
              true,
              true,
            );
            
            if (!viewFileSignedUrlResponse?.error) {
              // Update the attachment with the view URL
              prepareResponse.objects.attachments[attachment.id].url = viewFileSignedUrlResponse.signedUrl;
              console.log(`✅ Generated view URL for attachment ${attachment.id}:`, viewFileSignedUrlResponse.signedUrl);
            } else {
              console.error(`❌ Error generating view URL for attachment ${attachment.id}:`, viewFileSignedUrlResponse?.errorMsg);
            }
          } catch (error) {
            console.error(`❌ Error generating view URL for attachment ${attachment.id}:`, error);
          }
        }
      }

      // Handle copy to discrepancies if needed
      if (payloadCopy.copyToDiscrepancies.length) {
        try {
          const originalAttachments = Object.values(prepareResponse.objects.attachments)
            .filter((item) => item.id)
            .map((item) => item.id);

          const copyBody = new FormData();
          copyBody.append('originalAttachments', originalAttachments);
          copyBody.append('jsonBody', JSON.stringify(payloadCopy));

          const copyToDiscrepanciesResponse = yield apiRequest(
            'api/v1/documents/copy-files',
            { method: 'post', body: copyBody },
            false,
          );

          if (!copyToDiscrepanciesResponse.error) {
            // Ejecutar la copia a discrepancias
            yield put(
              apiSuccess(
                DiscrepancyDetailsTypes.COPY_FILES_TO_DISCREPANCIES_SUCCESS,
                copyToDiscrepanciesResponse,
              ),
            );

            // Ocultar el modal y navegar directamente
            yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_SUCCESS, prepareResponse));
            history.push(
              `/discrepancies/${payload.discrepancyId}/expedient/${newDocumentId}`,
            );
          }
        } catch (error) {
          yield put(
            apiSuccess(APP.SET_ERROR, {
              msg: 'Hubo un error en la copia de archivos a multiples discrepancias',
            }),
          );
        }
      } else {
        // Ocultar el modal y navegar directamente
        yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_SUCCESS, prepareResponse));
        history.push(`/discrepancies/${payload.discrepancyId}/expedient/${newDocumentId}`);
      }
    } catch (error) {
      console.log('ERROR IN DIRECT S3 UPLOAD:', error);
      yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_FAIL));
      yield put(apiSuccess(APP.SET_ERROR, { msg: 'Error uploading document with direct S3 method' }));
    }
  });
}

function* postDocument() {
  yield takeLeading(DiscrepancyDetailsTypes.CREATE_DOCUMENT, function* postDocumentApi(action) {
    const { history, ...payload } = action.payload;
    const payloadCopy = { ...payload };
    const body = new FormData();

    const { files, attachments } = payloadCopy;

    delete payloadCopy.files;
    delete payloadCopy.attachments;

    const stringifyPayload = JSON.stringify(payloadCopy);

    const mainRandom = uuidv4().split('-')[0];
    const mainRandomWord = uuidv4().split('-')[0];
    const mainRandomConfidential = uuidv4().split('-')[0];

    const [publicFile, wordFile, confidentialFile] = files;
    const testTotalFiles = [publicFile, wordFile, confidentialFile];

    body.append('files[]', publicFile, `${mainRandom}_${publicFile?.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}`);
    body.append(
      'files[]',
      wordFile || new Blob(),
      wordFile ? `${mainRandomWord}_${wordFile.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}` : undefined,
    );
    body.append(
      'files[]',
      confidentialFile || new Blob(),
      confidentialFile ? `${mainRandomConfidential}_${confidentialFile.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}` : undefined,
    );

    attachments.forEach((attachment) => {
      if (attachment) {
        const random = uuidv4().split('-')[0];
        const randomPdf = uuidv4().split('-')[0];
        // const randomWord = uuidv4().split('-')[0];
        const randomConfidential = uuidv4().split('-')[0];
        const { privateAtt, publicAtt } = attachment;
        if (privateAtt) {
          testTotalFiles.push(attachment.privateAtt);
        }
        if (publicAtt) {
          testTotalFiles.push(attachment.publicAtt);
        }
        body.append(
          `attachments[${random}]`,
          publicAtt,
          publicAtt ? `${randomPdf}_${publicAtt.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}` : undefined,
        );
        body.append(`attachments[${random}]`, new Blob(), null);
        body.append(
          `attachments[${random}]`,
          privateAtt || new Blob(),
          privateAtt ? `${randomConfidential}_${privateAtt.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}` : undefined,
        );
      }
    });

    body.append('jsonBody', stringifyPayload);

    const response = yield apiRequest('api/v1/documents', { method: 'post', body }, false);

    if (!response.error) {
      const attachmentsSaved = Object.values(response.objects.attachments).filter(
        (attachment) => attachment.id,
      );
      const pairedFiles = pairFiles(testTotalFiles, attachmentsSaved);
      for (const singleFile of pairedFiles) {
        const { contentType, id, content } = singleFile.savedInDb;
        const { file } = singleFile;
        let body = JSON.stringify({
          fileName: content,
          attachment: true,
          fileType: contentType,
          id,
        });
        try {
          const signedUrlResponse = yield call(
            apiRequest,
            `api/v1/s3/get_presigned_url`,
            { method: 'post', body },
            true,
            true,
          );
          if (signedUrlResponse?.error) {
            throw new Error('Error obteniendo signedUrl para guardar el archivo');
          }
          yield fetch(decodeURI(signedUrlResponse?.signedUrl), {
            method: 'put',
            headers: {
              'Content-Type': contentType,
              'Access-Control-Allow-Headers': 'Content-Type',
              'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
              'Access-Control-Allow-Origin': '*',
            },
            body: file,
          });
          body = JSON.stringify({
            id,
            fileName: content,
            contentType,
            attachment: true,
          });
          const viewFileSignedUrlResponse = yield apiRequest(
            `api/v1/s3/get_presigned_url_view`,
            {
              method: 'post',
              body,
            },
            true,
            true,
          );
          if (viewFileSignedUrlResponse?.error) {
            throw new Error('Error obteniendo signedUrl para guardar el archivo');
          }
          response.objects.attachments[id].url = viewFileSignedUrlResponse.signedUrl;
        } catch (error) {
          console.log(`ERROR SUBIENDO DOCUMENTO`, error);
          yield apiRequest(
            `api/v1/documents/${response.newDocumentId}`,
            { method: 'delete' },
            true,
            true,
          );
          yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_FAIL, response));
          yield put(
            apiSuccess(APP.SET_ERROR, { msg: 'Hubo un error subiendo el documento al sistema' }),
          );
          return;
        }
      }

      if (payloadCopy.copyToDiscrepancies.length) {
        try {
          const originalAttachments = Object.values(response.objects.attachments)
            .filter((item) => item.id)
            .map((item) => item.id);

          body.append('originalAttachments', originalAttachments);

          const copyToDiscrepanciesResponse = yield apiRequest(
            'api/v1/documents/copy-files',
            { method: 'post', body: body },
            false,
          );

          if (!copyToDiscrepanciesResponse.error) {
            // Ejecutar la copia a discrepancias
            yield put(
              apiSuccess(
                DiscrepancyDetailsTypes.COPY_FILES_TO_DISCREPANCIES_SUCCESS,
                copyToDiscrepanciesResponse,
              ),
            );

            // Ocultar el modal y navegar directamente
            yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_SUCCESS, response));
            history.push(
              `/discrepancies/${payload.discrepancyId}/expedient/${response.newDocumentId}`,
            );
          }
        } catch (error) {
          yield put(
            apiSuccess(APP.SET_ERROR, {
              msg: 'Hubo un error en la copia de archivos a multiples discrepancias',
            }),
          );
        }
      } else {
        // Ocultar el modal y navegar directamente
        yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_SUCCESS, response));
        history.push(`/discrepancies/${payload.discrepancyId}/expedient/${response.newDocumentId}`);
      }
    } else {
      yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_DOCUMENT_FAIL, response));
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    }
  });
}

function* deleteDocument() {
  yield takeLatest(
    DiscrepancyDetailsTypes.DELETE_DOCUMENT,
    function* deleteDiscrepancyDocument(action) {
      const { documentId, history, currentDiscrepancyId, firstDocumentId } = action.payload;

      const response = yield apiRequest(
        `api/v1/documents/${documentId}`,
        { method: 'delete' },
        true,
        true,
      );

      if (response.error) {
        yield put(apiSuccess(DiscrepancyDetailsTypes.DELETE_DOCUMENT_FAIL));
        return yield put({ type: SET_ERROR, payload: { msg: response.errorMsg } });
      }

      yield put({ type: DELETE_DOCUMENT_SUCCESS, state: selectState('app'), payload: response });
      history.push(`/discrepancies/${currentDiscrepancyId}/expedient/${firstDocumentId}`);
      return yield put(apiSuccess(DiscrepancyDetailsTypes.DELETE_DOCUMENT_SUCCESS, response));
    },
  );
}

function* notifyDiscrepancy() {
  yield takeLatest(
    DiscrepancyDetailsTypes.NOTIFICATE_DISCREPANCY,
    function* deleteDiscrepancyDocument(action) {
      const { discrepancyId, value } = action.payload;

      const successFunction = (response) => ({
        type: DiscrepancyDetailsTypes.NOTIFICATE_DISCREPANCY_SUCCESS,
        payload: { ...response },
      });

      const failFunction = (response) => ({
        type: DiscrepancyDetailsTypes.NOTIFICATE_DISCREPANCY_FAIL,
        payload: { ...response },
      });

      const response = yield controlledCall(
        update,
        `api/v1/discrepancies/${discrepancyId}/notificate`,
        { notificate: value },
        successFunction,
        failFunction,
      );

      if (response.error) {
        yield put({ type: SET_ERROR, payload: { msg: response.errorMsg } });
      }
    },
  );
}

function* updateDocumentStepOne() {
  yield takeLatest(
    DiscrepancyDetailsTypes.UPDATE_DOCUMENTS_VALUES,
    function* updateDocumentStepOneGenerator(action) {
      const {
        documentTypeId,
        id,
        index,
        title,
        documentDate,
        legalEntityId,
        dateHourAudience,
        dateHourRegistrationDeadline,
        dateHourPresentationInterestedParty,
        dateHourObservationsDeadline,
      } = action.payload;

      const successFunction = (response) => ({
        type: DiscrepancyDetailsTypes.UPDATE_DOCUMENTS_VALUES_SUCCESS,
        payload: { ...response },
      });

      const failFunction = (response) => ({
        type: DiscrepancyDetailsTypes.UPDATE_DOCUMENTS_VALUES_FAIL,
        payload: { ...response },
      });

      const response = yield controlledCall(
        update,
        `api/v1/documents/${id}`,
        {
          documentTypeId,
          index,
          title,
          documentDate,
          legalEntityId,
          dateHourAudience,
          dateHourRegistrationDeadline,
          dateHourPresentationInterestedParty,
          dateHourObservationsDeadline,
        },
        successFunction,
        failFunction,
      );

      if (response.error) {
        yield put({ type: SET_ERROR, payload: { msg: response.errorMsg } });
      }
    },
  );
}

function* updateAttachmentFiles() {
  yield takeLatest(
    DiscrepancyDetailsTypes.UPDATE_ATTACHMENT_FILES,
    function* postDocumentApi(action) {
      const payloadCopy = { ...action.payload };
      const body = new FormData();

      const { files, documentId, oldFileNames } = payloadCopy;

      delete payloadCopy.files;

      const newPayload = {
        ...payloadCopy,
        deleteConfidential: files[2].deleteConfidential,
      };

      const stringifyPayload = JSON.stringify(newPayload);

      const mainRandom = uuidv4().split('-')[0];
      body.append(
        'files[]',
        files[0]?.size ? files[0] : new Blob(),
        files[0]?.size ? `${mainRandom}_${files[0]?.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}` : oldFileNames[0],
      );

      if (files[1]?.size) {
        const mainRandomWord = uuidv4().split('-')[0];
        body.append('files[]', files[1], `${mainRandomWord}_${files[1]?.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}` || oldFileNames[1]);
      } else {
        body.append('files[]', new Blob(), null);
      }

      if (files[2].file?.size) {
        const mainRandomConfidential = uuidv4().split('-')[0];
        body.append(
          'files[]',
          files[2].file,
          `${mainRandomConfidential}_${files[2].file.name.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}` || oldFileNames[2],
        );
      } else {
        body.append('files[]', new Blob(), null);
      }

      body.append('jsonBody', stringifyPayload);

      const response = yield apiRequest(
        `api/v1/documents/${documentId}/attachments`,
        { method: 'put', body },
        false,
      );

      if (!response.error) {
        const filesUpdated = Object.values(response.objects.attachments).filter(
          (attachment) => attachment.id,
        );
        const pairedFiles = pairFiles(files, filesUpdated);
        for (const singleFile of pairedFiles) {
          const { contentType, id, content } = singleFile.savedInDb;
          const { file } = singleFile;
          let body = JSON.stringify({
            fileName: content,
            attachment: true,
            fileType: contentType,
            id,
          });
          try {
            const signedUrlResponse = yield call(
              apiRequest,
              `api/v1/s3/get_presigned_url`,
              { method: 'post', body },
              true,
              true,
            );
            if (signedUrlResponse?.error) {
              throw new Error('Error obteniendo signedUrl para guardar el archivo');
            }
            yield fetch(decodeURI(signedUrlResponse?.signedUrl), {
              method: 'put',
              headers: {
                'Content-Type': contentType,
                'Access-Control-Allow-Headers': 'Content-Type',
                'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
                'Access-Control-Allow-Origin': '*',
              },
              body: file,
            });
            body = JSON.stringify({
              id,
              fileName: content,
              contentType,
              attachment: true,
            });
            const viewFileSignedUrlResponse = yield apiRequest(
              `api/v1/s3/get_presigned_url_view`,
              {
                method: 'post',
                body,
              },
              true,
              true,
            );
            if (viewFileSignedUrlResponse?.error) {
              throw new Error('Error obteniendo signedUrl para guardar el archivo');
            }
            response.objects.attachments[id].url = viewFileSignedUrlResponse.signedUrl;
          } catch (error) {
            const body = new FormData();
            const stringifyPayload = JSON.stringify({
              ...newPayload,
              pageNumberMainpdf: newPayload.oldPageNumberMainPdf,
            });
            oldFileNames.forEach((oldFile) => {
              if (oldFile) {
                body.append('files[]', new Blob(), oldFile);
              } else {
                body.append('files[]', new Blob(), null);
              }
            });
            body.append('jsonBody', stringifyPayload);
            const responseTest = yield apiRequest(
              `api/v1/documents/${documentId}/attachments`,
              { method: 'put', body },
              false,
            );

            console.log(`ERROR SUBIENDO ARCHIVO`, error);
            yield put(apiSuccess(DiscrepancyDetailsTypes.UPDATE_ATTACHMENT_FILES_FAIL, response));
            yield put(apiSuccess(APP.SET_ERROR, { msg: 'Falló la carga del documento' }));
            return;
          }
        }
        const filesUnchanged = [];
        const attachmentsSaved = Object.values(response.objects.attachments);
        attachmentsSaved.forEach((attachmentSaved) => {
          const oldFileNamesCopy = [...oldFileNames];
          // We remove the first file name of the array (mainPdfFile), because we will always update it if the user only updates the mainPdfFilePageNumber but (...)
          // (...) not the mainPdfFile
          oldFileNamesCopy.shift();
          if (oldFileNamesCopy.includes(attachmentSaved.content)) {
            delete response.objects.attachments[`${attachmentSaved.id}`];
          }
        });
        yield put(apiSuccess(DiscrepancyDetailsTypes.UPDATE_ATTACHMENT_FILES_SUCCESS, response));
        yield put(
          apiSuccess(APP.SET_SUCCESS, {
            msg: 'Documentos principales actualizados correctamente',
          }),
        );
      } else {
        yield put(apiSuccess(DiscrepancyDetailsTypes.UPDATE_ATTACHMENT_FILES_FAIL, response));
        yield put(apiSuccess(APP.SET_ERROR, { msg: 'Falló la carga del documento' }));
      }
    },
  );
}

function* fetchDeleteAttachmentPart() {
  yield takeLatest(
    DiscrepancyDetailsTypes.DELETE_ATTACHMENT_PART,
    function* deleteAttachmentPart(action) {
      const { id } = action.payload;

      const response = yield apiRequest(
        `api/v1/attachmentParts/${id}`,
        { method: 'delete' },
        true,
        true,
      );

      if (response.error) {
        yield put(apiSuccess(DiscrepancyDetailsTypes.DELETE_ATTACHMENT_PART_FAIL));
        yield put({ type: SET_ERROR, payload: { msg: response.errorMsg } });
      } else {
        yield put(
          apiSuccess(APP.SET_SUCCESS, {
            msg: 'Adjunto eliminado correctamente.',
          }),
        );
        yield put(apiSuccess(DiscrepancyDetailsTypes.DELETE_ATTACHMENT_PART_SUCCESS, response));
      }
    },
  );
}

function* updateOrCreateAttachmentPart(action, updateMethod = false) {
  const payloadCopy = { ...action.payload };

  const body = new FormData();

  const {
    publicFile,
    privateFile,
    attachmentPartId,
    defaultPrivateAttachment,
    defaultPublicAttachment,
  } = payloadCopy;
  delete payloadCopy.publicFile;
  delete payloadCopy.privateFile;

  if (!updateMethod) {
    delete payloadCopy.attachmentPartId;
    delete payloadCopy.removeConfidential;
  }

  const successType = updateMethod
    ? DiscrepancyDetailsTypes.UPDATE_ATTACHMENT_PART_SUCCESS
    : DiscrepancyDetailsTypes.CREATE_ATTACHMENT_PART_SUCCESS;

  const failType = updateMethod
    ? DiscrepancyDetailsTypes.UPDATE_ATTACHMENT_PART_FAIL
    : DiscrepancyDetailsTypes.CREATE_ATTACHMENT_PART_FAIL;

  const files = [publicFile, privateFile];

  const stringifyPayload = JSON.stringify(payloadCopy);

  files.forEach((file) => {
    if (file?.size) {
      const random = uuidv4().split('-')[0];
      body.append('files[]', file, `${random}_${file.name.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}`);
    } else {
      body.append(`files[]`, new Blob(), null);
    }
  });

  body.append('jsonBody', stringifyPayload);

  const response = yield apiRequest(
    updateMethod ? `api/v1/attachmentParts/${attachmentPartId}` : 'api/v1/attachmentParts',
    { method: updateMethod ? 'put' : 'post', body },
    false,
  );

  if (!response.error) {
    const filesUpdated = Object.values(response.objects.attachments);
    const pairedFiles = pairFiles(files, filesUpdated);
    for (const singleFile of pairedFiles) {
      const { contentType, id, content } = singleFile.savedInDb;
      const { file } = singleFile;
      let body = JSON.stringify({
        fileName: content,
        attachment: true,
        fileType: contentType,
        id,
      });
      try {
        const signedUrlResponse = yield call(
          apiRequest,
          `api/v1/s3/get_presigned_url`,
          { method: 'post', body },
          true,
          true,
        );
        if (signedUrlResponse?.error) {
          throw new Error('Error obteniendo signedUrl para guardar el archivo');
        }
        yield fetch(decodeURI(signedUrlResponse?.signedUrl), {
          method: 'put',
          headers: {
            'Content-Type': contentType,
            'Access-Control-Allow-Headers': 'Content-Type',
            'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
            'Access-Control-Allow-Origin': '*',
          },
          body: file,
        });
        body = JSON.stringify({
          id,
          fileName: content,
          contentType,
          attachment: true,
        });
        const viewFileSignedUrlResponse = yield apiRequest(
          `api/v1/s3/get_presigned_url_view`,
          {
            method: 'post',
            body,
          },
          true,
          true,
        );
        if (viewFileSignedUrlResponse?.error) {
          throw new Error('Error obteniendo signedUrl para guardar el archivo');
        }
        response.objects.attachments[id].url = viewFileSignedUrlResponse.signedUrl;
      } catch (error) {
        const stringifyPayload = JSON.stringify({
          ...payloadCopy,
          removeConfidential: false,
        });
        console.log(`ERROR SUBIENDO ARCHIVO`, error);
        if (updateMethod) {
          const body = new FormData();
          defaultPublicAttachment?.content
            ? body.append('files[]', new Blob(), defaultPublicAttachment?.content)
            : body.append('files[]', new Blob(), null);
          defaultPrivateAttachment?.content
            ? body.append('files[]', new Blob(), defaultPrivateAttachment?.content)
            : body.append('files[]', new Blob(), null);

          body.append('jsonBody', stringifyPayload);
          const response = yield apiRequest(
            `api/v1/attachmentParts/${attachmentPartId}`,
            { method: 'put', body },
            false,
          );
        } else {
          const attachmentPartIdCreated = Object.values(response.objects.attachmentParts)[0].id;
          const responseTest = yield apiRequest(
            `api/v1/attachmentParts/${attachmentPartIdCreated}`,
            { method: 'delete' },
            false,
          );
        }

        yield put(apiSuccess(failType, response));
        yield put(apiSuccess(APP.SET_ERROR, { msg: 'Error al subir el archivo a la plataforma' }));
        return;
      }
    }
    yield put(apiSuccess(DiscrepancyDetailsTypes.UPDATE_ATTACHMENT_FILES_SUCCESS, response));
    yield put(
      apiSuccess(APP.SET_SUCCESS, {
        msg: 'Documentos principales actualizados correctamente',
      }),
    );
    yield put(apiSuccess(successType, response));
    yield put(
      apiSuccess(APP.SET_SUCCESS, {
        msg: updateMethod ? 'Adjunto actualizado correctamente.' : 'Adjunto creado correctamente.',
      }),
    );
  } else {
    yield put(apiSuccess(failType, response));
    yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
  }
}

function* fetchUpdateAttachmentPart() {
  yield takeLatest(
    DiscrepancyDetailsTypes.UPDATE_ATTACHMENT_PART,
    function* updateAttachmentPart(action) {
      return yield updateOrCreateAttachmentPart(action, true);
    },
  );
}

function* fetchCreateAttachmentPart() {
  yield takeLatest(
    DiscrepancyDetailsTypes.CREATE_ATTACHMENT_PART,
    function* updateAttachmentPart(action) {
      return yield updateOrCreateAttachmentPart(action);
    },
  );
}

function* fetchCreateAudienceDocument() {
  yield takeLatest(
    DiscrepancyDetailsTypes.CREATE_AUDIENCE_DOCUMENT,
    function* createAudienceDocument(action) {
      const { history, ...payload } = action.payload;
      const body = new FormData();
      const stringifyPayload = JSON.stringify(payload);

      body.append('jsonBody', stringifyPayload);

      const response = yield apiRequest('api/v1/hearings', { method: 'post', body }, false);
      if (!response.error) {
        yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_AUDIENCE_DOCUMENT_SUCCESS, response));
        yield put(
          apiSuccess(APP.SET_SUCCESS, {
            msg: 'Audiencia creada correctamente.',
          }),
        );
        history.push(`/discrepancies/${payload.discrepancyId}/expedient/${response.newDocumentId}`);
      } else {
        yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_AUDIENCE_DOCUMENT_FAIL, response));
        yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
      }
    },
  );
}

function* createHearingPresentation() {
  yield takeLatest(
    DiscrepancyDetailsTypes.CREATE_HEARING_PRESENTATION,
    function* createHearingPresentationGenerator(action) {
      const { hearingFile, history, ...payload } = action.payload;
      const stringifyPayload = JSON.stringify(payload);

      const payloadCopy = { ...payload };
      const body = new FormData();
      const random = uuidv4().split('-')[0];

      body.append('jsonBody', stringifyPayload);
      body.append(`files[]`, hearingFile, `${random}_${hearingFile.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}`);
      const copyBody = body;

      const response = yield apiRequest(
        'api/v1/hearings/hearing_presentation',
        { method: 'post', body },
        false,
      );

      if (!response.error) {
        const fileSavedInDb = Object.values(response.objects.attachments)[0];
        const { contentType, id, content } = fileSavedInDb;
        let body = JSON.stringify({
          fileName: content,
          attachment: true,
          fileType: contentType,
          id,
        });
        try {
          const signedUrlResponse = yield call(
            apiRequest,
            `api/v1/s3/get_presigned_url`,
            { method: 'post', body },
            true,
            true,
          );
          if (signedUrlResponse?.error) {
            throw new Error('Error obteniendo signedUrl para guardar el archivo');
          }
          yield fetch(decodeURI(signedUrlResponse?.signedUrl), {
            method: 'put',
            headers: {
              'Content-Type': contentType,
              'Access-Control-Allow-Headers': 'Content-Type',
              'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
              'Access-Control-Allow-Origin': '*',
            },
            body: hearingFile,
          });
          body = JSON.stringify({
            id,
            fileName: content,
            contentType,
            attachment: true,
          });
          const viewFileSignedUrlResponse = yield apiRequest(
            `api/v1/s3/get_presigned_url_view`,
            {
              method: 'post',
              body,
            },
            true,
            true,
          );
          if (viewFileSignedUrlResponse?.error) {
            throw new Error('Error obteniendo signedUrl para guardar el archivo');
          }
          response.objects.attachments[id].url = viewFileSignedUrlResponse.signedUrl;
        } catch (error) {
          console.log(error);
          yield apiRequest(
            `api/v1/documents/${response.newDocumentId}`,
            { method: 'delete' },
            true,
            true,
          );
          yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_HEARING_PRESENTATION_FAIL, response));
          yield put(
            apiSuccess(APP.SET_ERROR, { msg: 'Hubo un error al subir el archivo a la plataforma' }),
          );
          return;
        }

        if (payloadCopy.copyToDiscrepancies.length) {
          try {
            const originalAttachments = Object.values(response.objects.attachments)
              .filter((item) => item.id)
              .map((item) => item.id);

            copyBody.append('originalAttachments', originalAttachments);

            const copyToDiscrepanciesResponse = yield apiRequest(
              'api/v1/documents/copy-files',
              { method: 'post', body: copyBody },
              false,
            );

            if (!copyToDiscrepanciesResponse.error) {
              yield put(
                apiSuccess(DiscrepancyDetailsTypes.CREATE_HEARING_PRESENTATION_SUCCESS, response),
              );
              yield put(
                apiSuccess(APP.SET_SUCCESS, {
                  msg: 'Audiencia creada correctamente.',
                }),
              );
              yield put(
                apiSuccess(
                  DiscrepancyDetailsTypes.COPY_FILES_TO_DISCREPANCIES_SUCCESS,
                  copyToDiscrepanciesResponse,
                ),
              );
              history.push(
                `/discrepancies/${payload.discrepancyId}/expedient/${response.newDocumentId}`,
              );
            }
          } catch (error) {
            yield put(
              apiSuccess(APP.SET_ERROR, {
                msg: 'Hubo un error en la copia de archivos a multiples discrepancias',
              }),
            );
          }
        } else {
          yield put(
            apiSuccess(DiscrepancyDetailsTypes.CREATE_HEARING_PRESENTATION_SUCCESS, response),
          );
          yield put(
            apiSuccess(APP.SET_SUCCESS, {
              msg: 'Audiencia creada correctamente.',
            }),
          );
          history.push(
            `/discrepancies/${payload.discrepancyId}/expedient/${response.newDocumentId}`,
          );
        }
      } else {
        yield put(apiSuccess(DiscrepancyDetailsTypes.CREATE_HEARING_PRESENTATION_FAIL, response));
        yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
      }
    },
  );
}

export default function* DiscrepancyDetailsSaga() {
  yield spawn(getDiscrepancy);
  yield spawn(getHearingParticipationsFile);
  yield spawn(makeHearingParticipationsFile);
  yield spawn(postDocument);
  yield spawn(postDocumentDirectS3);
  yield spawn(deleteDocument);
  yield spawn(notifyDiscrepancy);
  yield spawn(updateDocumentStepOne);
  yield spawn(updateAttachmentFiles);
  yield spawn(fetchDeleteAttachmentPart);
  yield spawn(fetchUpdateAttachmentPart);
  yield spawn(fetchCreateAttachmentPart);
  yield spawn(fetchCreateAudienceDocument);
  yield spawn(createHearingPresentation);
  yield spawn(downloadAllFilesSaga);
}
